<template>
  <div class="overview-container">
    <base-page-title
      title="Moments"
      subtitle="The moments in the video where participants experienced significant increases in emotions or eye movements"
    ></base-page-title>
    <div class="container" v-if="testData !== null">
      <bg-video-player
        :video="testData.video"
        :getRef="setVideoRef"
        :currentTime="currentTime"
        :timeUpdate="timeUpdate"
        :newScrubberTime="newScrubberTime"
        :updateMetaData="handleVideoLoad"
        @playerPaused="resetTimeout"
      />
      <div class="chart-container" v-if="videoRef && datasets">
        <bg-moment-chart
          :datasets="datasets"
          :data="testState"
          :test="testData"
          :video="videoRef"
          @selected="handleBubbleClick"
          :selectedEmotion="currentEmotion"
        />
        <bg-emotion-chart-labels
          v-if="testData && duration"
          :data="testData"
          :duration="duration"
        />
        <bg-scrubber
          :key="scrubberKey"
          :scrubberTimeChange="setNewTime"
          v-if="videoRef"
          :currentTime="currentTime"
          :videoRef="videoRef"
          mouseDownTrigger
        />
      </div>
      <div class="neuro-container">
        <base-page-section-title
          bold-title="Neurofeedback "
          normal-title="for selected Moment"
        >
        </base-page-section-title>
        <div class="feedback-container" v-if="feedbackList.length > 0">
          <bg-detail-card
            v-for="feedback in feedbackList"
            v-bind:key="feedback.id"
            v-bind="{ ...feedback }"
            :text="feedback.comment"
            :detail="feedback.emotion"
          />
        </div>
        <div class="no-feedback" v-if="feedbackList.length === 0">
          No neurofeedback available.
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$green: var(--bg-color-green);
$pink: var(--bg-color-pink);
$yellow: var(--bg-color-yellow);
.header-container {
  display: flex;
  max-width: 800px;
}

.legend {
  flex-direction: row-reverse;
  padding-bottom: 20px;
}

.chart-container {
  width: 100%;
  height: 160px;
  position: relative;
  margin-top: 15px;
  margin-bottom: 90px;
}

.emotion-chart {
  margin-bottom: 25px;
}

.overview-container,
.container {
  max-width: 800px;
  width: 100%;
}

.video-container {
  position: relative;
}

.stats-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  width: 100%;
  justify-content: flex-end;
}

.stat {
  margin-right: 50px;
  display: flex;
  flex-direction: column;
}

.stat-question {
  font-weight: 300;
}

.stat-answer {
  font-weight: 700;
  font-size: 24px;
  color: var(--bg-color-text-secondary);
}

.nav,
.navButtons {
  display: flex;
}

.nav {
  max-width: 70%;
}

.navButton {
  margin-left: 7px;
}

.consultant-notes {
  padding: 20px;
  background-color: white;
  border-radius: var(--bg-border-radius);
  margin-bottom: 200px;
}
.neuro-container {
  margin-top: 100px;
  min-height: 300px;
}
</style>

<script>
import moment from "moment";
import BgDetailCard from "@/components/bg-detail-card";
import BgMomentChart from "@/components/bg-moment-chart";
import BgVideoPlayer from "@/components/bg-video-chart/bg-video-player";
import BgEmotionChartLabels from "@/components/bg-moment-chart/bg-chart-labels";
import BgScrubber from "@/components/bg-moment-chart/bg-scrubber";
import BgTypography from "@/directives/bg-typography";
import BasePageTitle from "@/components/base-page-title";
import BasePageSectionTitle from "@/components/base-page-section-title";
import API from "@/api";
import { mapState } from "vuex";
export default {
  name: "overview",
  components: {
    BgMomentChart,
    BgVideoPlayer,
    BgScrubber,
    BgDetailCard,
    BgEmotionChartLabels,
    BasePageSectionTitle,
    BasePageTitle,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      currentMoment: null,
      testData: null,
      videoRef: null,
      totalTime: 0,
      currentTime: 0,
      newScrubberTime: 0,
      scrubberKey: 2,
      datasets: [],
      selectedScene: null,
      duration: null,
      selectedMoment: null,
      currentTimeoutId: null,
      currentEmotion: null,
    };
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
    }),
    feedbackList() {
      if (!this.selectedMoment) {
        return [];
      }
      const filteredFeedback = this.testData.significant_points
        .filter((sp) => sp.comment !== null)
        .filter((sp) => sp.emotion === this.selectedMoment.emotion)
        .filter((sp) => {
          return (
            this.selectedMoment.start < sp.timestamp_top &&
            sp.timestamp_top < this.selectedMoment.end
          );
        });

      return filteredFeedback;
    },
  },
  watch: {
    "$route.params.id": function () {
      this.fetchData();
    },
  },
  methods: {
    handleBubbleClick(newMoment, startTime, endTime, duration, emotion) {
      this.currentEmotion = emotion;
      this.selectedMoment = newMoment;
      if (this.currentTimeoutId) {
        clearTimeout(this.currentTimeoutId);
      }
      this.setNewTime(startTime);
      this.videoRef.play();
      this.currentTimeoutId = setTimeout(() => {
        this.currentEmotion = null;
        this.videoRef.pause();
        this.setNewTime(endTime);
      }, duration);
    },
    handleSceneSelection(value) {
      this.selectedScene = value;
    },
    fetchData() {
      const fakeId = this.testState.participants.find(
        (p) => p.type === "video_heatmap"
      ).id;
      const testId = this.$route.params.id;
      API.test.getFakeParticipant(testId, fakeId).then((res) => {
        const scenes = res.data.sections.map((s) => {
          return {
            label: s.label.name,
            value: s.id,
          };
        });

        this.options = scenes;
        this.selectedScene = scenes[0];

        this.testData = {
          ...res.data,
        };

        this.datasets = [
          this.prepareDataset("frustration", "Frustration"),
          this.prepareDataset("joy", "Joy"),
          this.prepareDataset("attention", "Attention"),
          this.prepareDataset("spread", "Spread"),
          this.prepareDataset("fluency", "Eye movements"),
        ];
      });
    },
    prepareDataset(emotion, label) {
      return {
        label: label,
        data: this.testData.moments.filter((e) => e.emotion === emotion),
      };
    },
    setVideoRef(ref) {
      this.videoRef = ref;
    },
    timeUpdate(time) {
      this.currentTime = time;
    },
    setNewTime(newTimePercentage) {
      const newTime = this.videoRef.duration * newTimePercentage;
      if (newTime === this.newScrubberTime) {
        // to handle the click on the same bubble
        this.newScrubberTime = this.newScrubberTime - 0.0001;
      } else {
        this.newScrubberTime = this.videoRef.duration * newTimePercentage;
      }
    },
    resetTimeout() {
      if (this.currentTimeoutId) {
        this.currentEmotion = null;
        clearTimeout(this.currentTimeoutId);
      }
    },
    handleVideoLoad(video) {
      this.duration = video.duration;
      if (video) {
        if (this.$route.query.timestamp) {
          const startTime = moment(this.testState.started_at);
          const endTime = moment(this.testState.ended_at);
          const startPos = moment(Number(this.$route.query.timestamp));
          const videoLength = endTime.diff(startTime, "seconds");
          const timeElapsed = startPos.diff(startTime, "seconds");
          this.setNewTime(timeElapsed / videoLength);
        }
        this.totalTime = video.duration;
      }
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
